/**
 * My Profile Settings Page
 *
 * Lets each user maintain a rich profile that captures professional
 * attributes (title, seniority, expertise, etc.) used for segment-based
 * analysis in reports.
 */

import { useState, useEffect, useRef } from 'react';
import {
  UserCircle,
  Save,
  Plus,
  X,
  ChevronDown,
  Info,
  ImagePlus,
  Trash2,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useMyRichProfile } from '../../../hooks/useUserRichProfile';
import { useUserStore } from '../../../stores/prioritization/userStore';
import {
  SENIORITY_LEVELS,
  JOB_FAMILIES,
  PROFILE_VISIBILITY_LABELS,
  type JobFamily,
  type SeniorityLevel,
  type ProfileVisibility,
} from '../../../types/prioritization/userProfile';
import { getSafeSupabaseClient } from '../../../lib/supabaseSync';
import {
  resizeImageFileToJpeg,
  uploadProfileAvatar,
  removeProfileAvatarObject,
} from '../../../lib/profileAvatar';

// ── Tag input ─────────────────────────────────────────────────────────────────

interface TagInputProps {
  label: string;
  hint?: string;
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
}

function TagInput({ label, hint, tags, onChange, placeholder = 'Add…' }: TagInputProps) {
  const [input, setInput] = useState('');

  const add = () => {
    const trimmed = input.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
    }
    setInput('');
  };

  const remove = (tag: string) => onChange(tags.filter((t) => t !== tag));

  return (
    <div>
      <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">{label}</label>
      {hint && <p className="text-xs text-[var(--text-ter)] mb-2">{hint}</p>}
      <div className="flex flex-wrap gap-1.5 min-h-[2.5rem] p-2 border border-[var(--rule)] bg-[var(--bg-raised)] focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
        {tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 text-primary-800 rounded text-xs font-medium"
          >
            {tag}
            <button
              type="button"
              onClick={() => remove(tag)}
              className="hover:text-primary-900 focus:outline-none"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              add();
            }
          }}
          placeholder={tags.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[8rem] text-sm outline-none bg-transparent placeholder-gray-400"
        />
      </div>
      {input.trim() && (
        <button
          type="button"
          onClick={add}
          className="mt-1 flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
        >
          <Plus className="w-3 h-3" />
          Add "{input.trim()}"
        </button>
      )}
    </div>
  );
}

// ── Select field ──────────────────────────────────────────────────────────────

interface SelectFieldProps<T extends string> {
  label: string;
  value: T | undefined;
  options: { value: T; label: string }[];
  onChange: (v: T | undefined) => void;
  placeholder?: string;
}

function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
  placeholder = '— not set —',
}: SelectFieldProps<T>) {
  return (
    <div>
      <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">{label}</label>
      <div className="relative">
        <select
          value={value ?? ''}
          onChange={(e) => onChange((e.target.value as T) || undefined)}
          className="block w-full appearance-none border border-[var(--rule)] bg-[var(--bg-raised)] py-2 pl-3 pr-8 text-sm text-[var(--text-pri)] focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">{placeholder}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-ter)]" />
      </div>
    </div>
  );
}

// ── Number field ──────────────────────────────────────────────────────────────

interface NumberFieldProps {
  label: string;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
}

function NumberField({ label, value, onChange, min = 0, max = 60, step = 0.5, suffix }: NumberFieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={value ?? ''}
          min={min}
          max={max}
          step={step}
          onChange={(e) => {
            const raw = parseFloat(e.target.value);
            onChange(isNaN(raw) ? undefined : raw);
          }}
          className="block w-28 border border-[var(--rule)] bg-[var(--bg-raised)] py-2 px-3 text-sm text-[var(--text-pri)] focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        {suffix && <span className="text-sm text-[var(--text-ter)]">{suffix}</span>}
      </div>
    </div>
  );
}

// ── Text field ────────────────────────────────────────────────────────────────

interface TextFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
}

function TextField({ label, value, onChange, placeholder, maxLength }: TextFieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="block w-full border border-[var(--rule)] bg-[var(--bg-raised)] py-2 px-3 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
      />
    </div>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-6 space-y-5">
      <h3 className="text-sm font-semibold text-[var(--text-pri)] uppercase tracking-wide">{title}</h3>
      {children}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function MyProfile() {
  const { profile, loading, saving, error, save } = useMyRichProfile();
  const userName = useUserStore((s) => s.userName);
  const userEmail = useUserStore((s) => s.userEmail);
  const userId = useUserStore((s) => s.userId);
  const userAvatarUrl = useUserStore((s) => s.userAvatarUrl);
  const setUserAvatarUrl = useUserStore((s) => s.setUserAvatarUrl);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  // Form state — mirrors UserRichProfile fields
  const [jobTitle, setJobTitle] = useState('');
  const [jobFamily, setJobFamily] = useState<JobFamily | undefined>();
  const [seniorityLevel, setSeniorityLevel] = useState<SeniorityLevel | undefined>();
  const [orgLevel, setOrgLevel] = useState('');
  const [yearsInCurrentRole, setYearsInCurrentRole] = useState<number | undefined>();
  const [yearsAtCompany, setYearsAtCompany] = useState<number | undefined>();
  const [yearsTotalExperience, setYearsTotalExperience] = useState<number | undefined>();
  const [expertiseAreas, setExpertiseAreas] = useState<string[]>([]);
  const [productAreas, setProductAreas] = useState<string[]>([]);
  const [department, setDepartment] = useState('');
  const [teamName, setTeamName] = useState('');
  const [location, setLocation] = useState('');
  const [timezone, setTimezone] = useState('');
  const [bio, setBio] = useState('');
  const [profileVisibility, setProfileVisibility] = useState<ProfileVisibility>('workspace');

  const [saved, setSaved] = useState(false);

  // Populate form when profile loads
  useEffect(() => {
    if (!profile) return;
    setJobTitle(profile.jobTitle ?? '');
    setJobFamily(profile.jobFamily);
    setSeniorityLevel(profile.seniorityLevel);
    setOrgLevel(profile.orgLevel ?? '');
    setYearsInCurrentRole(profile.yearsInCurrentRole);
    setYearsAtCompany(profile.yearsAtCompany);
    setYearsTotalExperience(profile.yearsTotalExperience);
    setExpertiseAreas(profile.expertiseAreas);
    setProductAreas(profile.productAreas);
    setDepartment(profile.department ?? '');
    setTeamName(profile.teamName ?? '');
    setLocation(profile.location ?? '');
    setTimezone(profile.timezone ?? '');
    setBio(profile.bio ?? '');
    setProfileVisibility(profile.profileVisibility);
  }, [profile]);

  const displayAvatarUrl = profile?.avatarUrl ?? userAvatarUrl ?? undefined;

  const handleAvatarPick = () => fileInputRef.current?.click();

  const handleAvatarFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !userId || userId === 'unknown') return;
    const client = getSafeSupabaseClient();
    if (!client) {
      setAvatarError('Not signed in.');
      return;
    }
    setAvatarError(null);
    setAvatarUploading(true);
    try {
      const blob = await resizeImageFileToJpeg(file);
      const up = await uploadProfileAvatar(client, userId, blob);
      if ('error' in up) {
        setAvatarError(up.error);
        return;
      }
      const ok = await save({ avatarUrl: up.publicUrl });
      if (!ok) {
        setAvatarError('Could not save profile photo. Try again.');
      } else {
        setUserAvatarUrl(up.publicUrl);
      }
    } catch (err) {
      setAvatarError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!window.confirm('Remove your profile photo?')) return;
    const client = getSafeSupabaseClient();
    if (!userId || userId === 'unknown') return;
    setAvatarError(null);
    setAvatarUploading(true);
    try {
      if (client) {
        await removeProfileAvatarObject(client, userId);
      }
      const ok = await save({ avatarUrl: null });
      if (ok) {
        setUserAvatarUrl(null);
      } else {
        setAvatarError('Could not update profile.');
      }
    } catch (err) {
      setAvatarError(err instanceof Error ? err.message : 'Could not remove photo');
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleSave = async () => {
    const ok = await save({
      jobTitle:             jobTitle || undefined,
      jobFamily,
      seniorityLevel,
      orgLevel:             orgLevel || undefined,
      yearsInCurrentRole,
      yearsAtCompany,
      yearsTotalExperience,
      expertiseAreas,
      productAreas,
      department:           department || undefined,
      teamName:             teamName || undefined,
      location:             location || undefined,
      timezone:             timezone || undefined,
      bio:                  bio || undefined,
      profileVisibility,
    });
    if (ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const seniorityOptions = SENIORITY_LEVELS;
  const familyOptions = JOB_FAMILIES.map((f) => ({ value: f as JobFamily, label: f }));
  const visibilityOptions = (Object.keys(PROFILE_VISIBILITY_LABELS) as ProfileVisibility[]).map(
    (v) => ({ value: v, label: PROFILE_VISIBILITY_LABELS[v] })
  );

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<UserCircle className="h-4 w-4 text-[var(--text-sec)]" />}
        iconBg="bg-[var(--hover-tint)]"
        title="My Profile"
        subtitle="Professional attributes that help contextualize your contributions in reports and analysis"
        actions={
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving…' : saved ? 'Saved!' : 'Save profile'}
          </button>
        }
      />

      {/* Identity + profile photo */}
      <div className="border border-[var(--rule)] bg-[var(--surface)] px-6 py-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4 min-w-0">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={handleAvatarFile}
          />
          {displayAvatarUrl ? (
            <img
              key={profile?.updatedAt?.getTime() ?? displayAvatarUrl}
              src={displayAvatarUrl}
              alt=""
              className="h-14 w-14 rounded-full object-cover border border-[var(--rule)] flex-shrink-0 bg-[var(--bg-raised)]"
            />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-700 text-lg font-bold flex-shrink-0 border border-[var(--rule)]">
              {userName?.charAt(0)?.toUpperCase() ?? '?'}
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[var(--text-pri)] truncate">{userName}</p>
            <p className="text-xs text-[var(--text-ter)] truncate">{userEmail}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleAvatarPick}
            disabled={avatarUploading || userId === 'unknown'}
            className="inline-flex items-center gap-2 border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] disabled:opacity-50"
          >
            <ImagePlus className="h-3.5 w-3.5" />
            {avatarUploading ? 'Working…' : displayAvatarUrl ? 'Change photo' : 'Add photo'}
          </button>
          {displayAvatarUrl && (
            <button
              type="button"
              onClick={handleRemoveAvatar}
              disabled={avatarUploading}
              className="inline-flex items-center gap-2 border border-[var(--coral)] bg-[var(--bg-raised)] px-3 py-1.5 text-xs font-medium text-[var(--coral)] hover:bg-[var(--coral-dim)] disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Remove
            </button>
          )}
        </div>
      </div>
      {avatarError && (
        <div className="border border-[var(--coral)] bg-[var(--coral-dim)] px-4 py-2 text-sm text-[var(--coral)]">{avatarError}</div>
      )}

      {loading ? (
        <div className="py-12 text-center text-sm text-[var(--text-ter)]">Loading profile…</div>
      ) : (
        <div className="space-y-5">
          {error && (
            <div className="border border-[var(--coral)] bg-[var(--coral-dim)] px-4 py-3 text-sm text-[var(--coral)]">
              {error}
            </div>
          )}

          {/* ── Role & Seniority ── */}
          <Section title="Role & Seniority">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextField
                label="Job title"
                value={jobTitle}
                onChange={setJobTitle}
                placeholder="e.g. Staff Product Manager"
                maxLength={120}
              />
              <SelectField
                label="Function / Job family"
                value={jobFamily}
                options={familyOptions}
                onChange={setJobFamily}
              />
              <SelectField
                label="Seniority level"
                value={seniorityLevel}
                options={seniorityOptions}
                onChange={setSeniorityLevel}
              />
              <TextField
                label="Org level code"
                value={orgLevel}
                onChange={setOrgLevel}
                placeholder="e.g. L5, IC6, M3, P4"
                maxLength={20}
              />
            </div>
          </Section>

          {/* ── Experience ── */}
          <Section title="Experience">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <NumberField
                label="Years in current role"
                value={yearsInCurrentRole}
                onChange={setYearsInCurrentRole}
                suffix="yrs"
              />
              <NumberField
                label="Years at company"
                value={yearsAtCompany}
                onChange={setYearsAtCompany}
                suffix="yrs"
              />
              <NumberField
                label="Total years of experience"
                value={yearsTotalExperience}
                onChange={setYearsTotalExperience}
                suffix="yrs"
              />
            </div>
          </Section>

          {/* ── Expertise ── */}
          <Section title="Expertise">
            <TagInput
              label="Domain expertise"
              hint="Domains you work in or have deep knowledge of — press Enter or comma to add."
              tags={expertiseAreas}
              onChange={setExpertiseAreas}
              placeholder="e.g. payments, security, ML, growth…"
            />
            <TagInput
              label="Product areas"
              hint="Product surfaces or areas you own, work on, or are expert in."
              tags={productAreas}
              onChange={setProductAreas}
              placeholder="e.g. mobile, checkout, API platform…"
            />
          </Section>

          {/* ── Org Context ── */}
          <Section title="Organization">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextField
                label="Department"
                value={department}
                onChange={setDepartment}
                placeholder="e.g. Platform Engineering"
                maxLength={80}
              />
              <TextField
                label="Team name"
                value={teamName}
                onChange={setTeamName}
                placeholder="e.g. Checkout Foundations"
                maxLength={80}
              />
            </div>
          </Section>

          {/* ── Location ── */}
          <Section title="Location">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextField
                label="Location"
                value={location}
                onChange={setLocation}
                placeholder="e.g. San Francisco, CA"
                maxLength={100}
              />
              <TextField
                label="Timezone"
                value={timezone}
                onChange={setTimezone}
                placeholder="e.g. America/Los_Angeles"
                maxLength={60}
              />
            </div>
          </Section>

          {/* ── Bio ── */}
          <Section title="Bio">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Short bio</label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="A sentence or two about your role, focus area, or perspective…"
                className="block w-full border border-[var(--rule)] bg-[var(--bg-raised)] py-2 px-3 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 resize-y"
              />
              <p className="mt-1 text-xs text-[var(--text-ter)] text-right">{bio.length}/500</p>
            </div>
          </Section>

          {/* ── Visibility ── */}
          <Section title="Visibility">
            <div className="flex items-start gap-3">
              <Info className="w-4 h-4 text-[var(--text-ter)] mt-0.5 flex-shrink-0" />
              <p className="text-xs text-[var(--text-ter)]">
                Controls who can see your profile attributes in reports and analysis views.
                Your name and email are always visible to workspace members regardless of this setting.
              </p>
            </div>
            <SelectField
              label="Who can see my profile"
              value={profileVisibility}
              options={visibilityOptions}
              onChange={(v) => setProfileVisibility(v ?? 'workspace')}
            />
          </Section>

          {/* Save button (bottom) */}
          <div className="flex justify-end pt-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-primary-600 px-5 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : saved ? 'Saved!' : 'Save profile'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
